import { Link, createFileRoute } from "@tanstack/react-router";
import { useChat } from "@ai-sdk/react";
import { Bot, Loader2, Send, Square, UserRound, Wrench } from "lucide-react";
import { DefaultChatTransport } from "ai";
import { useMemo, useRef, useState } from "react";
import { Streamdown } from "streamdown";

import { authClient } from "#/lib/auth-client";
import { SITE_NAME, canonicalLink, seoMeta } from "#/lib/seo";

import type { UIMessage } from "ai";
import type { SyntheticEvent } from "react";

type ToolPart = Extract<UIMessage["parts"][number], { type: `tool-${string}` }>;

type MentionedBook = {
  slug: string;
  title: string;
  author: string;
  imageUrl: string | null;
};

const suggestedPrompts = [
  "Find episodes where guests mention product or design books.",
  "Which books come up most often?",
  "Show details for The Mom Test.",
];

export const Route = createFileRoute("/chat")({
  head: () => ({
    meta: seoMeta({
      title: `Chat | ${SITE_NAME}`,
      description: "Chat with the GDIY books archive.",
      path: "/chat",
    }),
    links: canonicalLink("/chat"),
  }),
  component: ChatPage,
});

function ChatPage() {
  const { data: session, isPending } = authClient.useSession();

  if (isPending) {
    return (
      <main className="chat-page">
        <div className="page-wrap chat-loading">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          <span>Loading chat</span>
        </div>
      </main>
    );
  }

  if (!session?.user) {
    return (
      <main className="chat-page">
        <section className="page-wrap chat-gate" aria-labelledby="chat-gate-heading">
          <p className="eyebrow">Bookling agent</p>
          <h1 id="chat-gate-heading">Sign in to chat with GDIY books.</h1>
          <p>
            The public MCP endpoint is available to external clients. The in-app chat is gated to
            keep Workers AI usage accountable.
          </p>
          <Link to="/auth" className="button-primary">
            <UserRound className="h-4 w-4" aria-hidden="true" />
            Sign in
          </Link>
        </section>
      </main>
    );
  }

  return <ChatSurface />;
}

function ChatSurface() {
  const chat = useChat({
    transport: new DefaultChatTransport({ api: "/api/chat" }),
    experimental_throttle: 80,
  });
  const [prompt, setPrompt] = useState("");
  const transcriptRef = useRef<HTMLDivElement>(null);

  const isBusy = chat.status === "submitted" || chat.status === "streaming";
  const hasMessages = chat.messages.length > 0;
  const statusText = useMemo(() => {
    if (chat.status === "submitted") return "Thinking";
    if (chat.status === "streaming") return "Answering";
    if (chat.status === "error") return "Error";
    return null;
  }, [chat.status]);

  function send(event?: SyntheticEvent, text = prompt) {
    event?.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || isBusy) return;
    void chat.sendMessage({ text: trimmed });
    setPrompt("");
    requestAnimationFrame(() => {
      transcriptRef.current?.scrollTo({
        top: transcriptRef.current.scrollHeight,
        behavior: "smooth",
      });
    });
  }

  return (
    <main className="chat-page">
      <section className="chat-shell page-wrap" aria-label="Bookling chat">
        <header className="chat-topbar">
          <div>
            <p className="eyebrow">Bookling agent</p>
            <h1>Chat with GDIY books</h1>
          </div>
          <div className="chat-status" data-status={chat.status}>
            {isBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <span className="chat-status-dot" aria-hidden="true" />
            )}
            {statusText ? <span>{statusText}</span> : <span className="sr-only">Ready</span>}
          </div>
        </header>

        <div className="chat-transcript" ref={transcriptRef}>
          {!hasMessages ? (
            <div className="chat-empty">
              <Bot className="h-6 w-6" aria-hidden="true" />
              <h2>Start with a book, guest, episode, or theme.</h2>
              <div className="chat-suggestions" aria-label="Suggested prompts">
                {suggestedPrompts.map((item) => (
                  <button key={item} type="button" onClick={() => send(undefined, item)}>
                    {item}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            chat.messages.map((message, index) => (
              <ChatMessage
                key={message.id}
                message={message}
                isStreaming={chat.status === "streaming" && index === chat.messages.length - 1}
              />
            ))
          )}
          {chat.error ? <p className="chat-error">{chat.error.message}</p> : null}
        </div>

        <form className="chat-composer" onSubmit={send}>
          <label className="sr-only" htmlFor="chat-prompt">
            Ask Bookling
          </label>
          <textarea
            id="chat-prompt"
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                send(event);
              }
            }}
            placeholder="Ask about a book, guest, episode, or recommendation pattern"
            rows={1}
          />
          {isBusy ? (
            <button type="button" className="chat-icon-button" onClick={() => chat.stop()}>
              <Square className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only">Stop</span>
            </button>
          ) : (
            <button type="submit" className="chat-icon-button" disabled={!prompt.trim()}>
              <Send className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only">Send</span>
            </button>
          )}
        </form>
      </section>
    </main>
  );
}

function ChatMessage({ message, isStreaming }: { message: UIMessage; isStreaming: boolean }) {
  const isUser = message.role === "user";

  const mentionedBooks = useMemo(() => {
    if (isUser) return [];
    const harvested = new Map<string, MentionedBook>();
    for (const part of message.parts) {
      if (!isToolPart(part)) continue;
      if (part.state !== "output-available") continue;
      for (const book of harvestBooks(part.output)) {
        if (!book.slug || harvested.has(book.slug)) continue;
        if (!book.title) continue;
        harvested.set(book.slug, book);
      }
    }
    return Array.from(harvested.values()).slice(0, 8);
  }, [isUser, message.parts]);

  return (
    <article className={isUser ? "chat-message is-user" : "chat-message is-assistant"}>
      <div className="chat-avatar" aria-hidden="true">
        {isUser ? <UserRound className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
      </div>
      <div className="chat-message-body">
        {message.parts.map((part, index) => {
          if (part.type === "text") {
            if (isUser) {
              return (
                <p key={index} className="chat-user-text">
                  {part.text}
                </p>
              );
            }
            return (
              <div key={index} className="chat-markdown">
                <Streamdown animated isAnimating={isStreaming}>
                  {part.text}
                </Streamdown>
              </div>
            );
          }
          if (isToolPart(part)) {
            return <ToolCall key={index} part={part} />;
          }
          return null;
        })}
        {mentionedBooks.length > 0 ? <BookMentions books={mentionedBooks} /> : null}
      </div>
    </article>
  );
}

function BookMentions({ books }: { books: ReadonlyArray<MentionedBook> }) {
  return (
    <div className="chat-book-mentions" aria-label="Books mentioned">
      {books.map((book) => (
        <Link
          key={book.slug}
          to="/books/$slug"
          params={{ slug: book.slug }}
          className="chat-book-card"
          title={`${book.title} - ${book.author}`}
        >
          <div className="chat-book-cover">
            {book.imageUrl ? (
              <img src={book.imageUrl} alt="" loading="lazy" />
            ) : (
              <span aria-hidden="true">{book.title.slice(0, 1)}</span>
            )}
          </div>
          <div className="chat-book-meta">
            <strong>{book.title}</strong>
            <span>{book.author}</span>
          </div>
        </Link>
      ))}
    </div>
  );
}

function harvestBooks(value: unknown): Array<MentionedBook> {
  const out: Array<MentionedBook> = [];
  visit(value, out, new Set());
  return out;
}

function visit(node: unknown, out: Array<MentionedBook>, seen: Set<unknown>) {
  if (!node || typeof node !== "object") return;
  if (seen.has(node)) return;
  seen.add(node);
  if (Array.isArray(node)) {
    for (const item of node) visit(item, out, seen);
    return;
  }
  const obj = node as Record<string, unknown>;
  if (
    typeof obj.slug === "string" &&
    typeof obj.title === "string" &&
    typeof obj.author === "string"
  ) {
    out.push({
      slug: obj.slug,
      title: obj.title,
      author: obj.author,
      imageUrl: typeof obj.imageUrl === "string" ? obj.imageUrl : null,
    });
  }
  if (Array.isArray(obj.books)) {
    for (const item of obj.books) {
      if (item && typeof item === "object") {
        const book = item as Record<string, unknown>;
        if (
          typeof book.slug === "string" &&
          typeof book.title === "string" &&
          typeof book.author === "string"
        ) {
          out.push({
            slug: book.slug,
            title: book.title,
            author: book.author,
            imageUrl: typeof book.imageUrl === "string" ? book.imageUrl : null,
          });
        }
      }
    }
  }
  for (const value of Object.values(obj)) visit(value, out, seen);
}

function isToolPart(part: UIMessage["parts"][number]): part is ToolPart {
  return part.type.startsWith("tool-");
}

function ToolCall({ part }: { part: ToolPart }) {
  const name = part.type.replace("tool-", "");
  const isDone = part.state === "output-available";
  const isError = part.state === "output-error";

  return (
    <details className="chat-tool" open={!isDone && !isError}>
      <summary>
        <span>
          <Wrench className="h-4 w-4" aria-hidden="true" />
          {name}
        </span>
        <em>{part.state.replace(/-/g, " ")}</em>
      </summary>
      <pre>{formatToolPayload(part)}</pre>
    </details>
  );
}

function formatToolPayload(part: ToolPart) {
  if (part.state === "output-error") {
    return part.errorText;
  }
  return JSON.stringify(
    {
      input: part.input,
      output: part.state === "output-available" ? part.output : undefined,
    },
    null,
    2,
  );
}
